/**
@format
@flow
*/

import type { CroppedMediaHelperParams } from '@ziynx/flow-types/media';

/**
 * Function to create a new image from an image source
 */
const createImage = (url: string) => new Promise((resolve, reject) => {
  const image = new Image();
  image.setAttribute('crossOrigin', 'anonymous'); // Gotta have this to create image from existing media url
  image.addEventListener('load', () => resolve(image));
  image.addEventListener('error', (error: any) => {
    reject(error);
  });
  image.src = url;
});

const createVideo = (url: string) => new Promise((resolve, reject) => {
  const video = document.createElement('video');
  video.setAttribute('src', url);

  video.addEventListener('loadedmetadata', () => resolve(video));
  video.addEventListener('error', (error: any) => {
    reject(error);
  });
});

/**
 * Function used for rotation functionality of image
 */
const getRadianAngle = (rotation: number): number => (rotation * Math.PI) / 180;

/**
 * Function to retrieve cropper's image data and create a new image based on the image data
 * Currently exports as .jpeg
 * For reference, @see https://codesandbox.io/s/q8q1mnr01w?file=/src/index.js:974-1315
 */
export default async function getCroppedMedia({
  imageSource,
  croppedAreaPixels,
  type,
  allowRotation = false,
  rotation,
}: CroppedMediaHelperParams): Promise<any> {
  // Create new image / video based on cropped image passed in
  const media: any = type === 'video' ? await createVideo(imageSource) : await createImage(imageSource);
  // Create a blank canvas and get context
  const canvas = document.createElement('canvas');
  const canvasContext = canvas.getContext('2d');

  let mediaHeight; let
    mediaWidth;
  switch (type) {
  case 'video':
    mediaHeight = media.videoHeight;
    mediaWidth = media.videoWidth;
    break;
  case 'image':
  default:
    mediaHeight = media.height;
    mediaWidth = media.width;
    break;
  }

  const maxSize = Math.max(mediaWidth, mediaHeight);
  const safeArea = 2 * ((maxSize / 2) * Math.sqrt(2));

  // Set each dimensions to double largest dimension to allow for a safe area for the
  // image to rotate in without being clipped by canvas context
  canvas.width = safeArea;
  canvas.height = safeArea;

  if (allowRotation && rotation) {
    // Translate canvas context to a central location on image to allow rotating around the center.
    canvasContext.translate(safeArea / 2, safeArea / 2);
    canvasContext.rotate(getRadianAngle(rotation)); // Default rotation is 0
    canvasContext.translate(-safeArea / 2, -safeArea / 2);
  }

  // Draw new image with rotated image (if was rotated) and store data
  canvasContext.drawImage(
    media,
    safeArea / 2 - mediaWidth * 0.5,
    safeArea / 2 - mediaHeight * 0.5,
  );
  const data = canvasContext.getImageData(0, 0, safeArea, safeArea);

  // Set canvas width and height to cropped width and height passed in
  canvas.width = croppedAreaPixels.width;
  canvas.height = croppedAreaPixels.height;

  // Paste generated image with correct offsets for x, y crop values
  canvasContext.putImageData(
    data,
    Math.round(0 - safeArea / 2 + mediaWidth * 0.5 - croppedAreaPixels.x),
    Math.round(0 - safeArea / 2 + mediaHeight * 0.5 - croppedAreaPixels.y),
  );

  // Export cropped image
  switch (type) {
  case 'video':
    return new Promise((resolve) => {
      canvas.toBlob((file) => {
        resolve(URL.createObjectURL(file));
      }, 'video/mp4');
    });

  case 'image':
  default:
    return canvas.toDataURL('image/jpeg');
  }
}
